import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Smartphone, MessageSquareText, BadgeIndianRupee, ShieldCheck, Sparkles, ArrowRight, ArrowUpRight, ClipboardList, ChevronRight, BellRing, Users, CheckCircle2, Zap, Wallet, ThumbsUp, Star } from "lucide-react";
import { TaskCard } from "@/components/TaskCard";
import { StatusBadge } from "@/components/StatusBadge";
import { api, inr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STEPS = [
  { icon: Search, title: "Find a task", text: "Browse available app-testing tasks and pick one that interests you." },
  { icon: Smartphone, title: "Install & explore", text: "Install the app from the Google Play Store and use it as instructed." },
  { icon: MessageSquareText, title: "Share honest feedback", text: "Complete the research task and submit your genuine experience with proof." },
  { icon: BadgeIndianRupee, title: "Get rewarded", text: "Once our team verifies your submission, the reward is credited to your wallet." },
];

const WHY = [
  { icon: BadgeIndianRupee, title: "Real rewards via UPI", text: "Earn money for every eligible task and withdraw straight to your UPI ID." },
  { icon: Zap, title: "Quick 10–15 min tasks", text: "No skills needed. Just install, explore and share what you honestly think." },
  { icon: ThumbsUp, title: "Honesty is rewarded", text: "Positive, neutral or critical — your genuine opinion is what we pay for." },
  { icon: ShieldCheck, title: "Transparent tracking", text: "Live status for every submission, reward and payout. No hidden rules." },
];

function NotificationPrompt() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default" && !localStorage.getItem("notif-dismissed")) {
      setShow(true);
    }
  }, []);
  if (!show) return null;
  const allow = async () => {
    try { await Notification.requestPermission(); } catch {}
    localStorage.setItem("notif-dismissed", "1");
    setShow(false);
  };
  const later = () => {
    localStorage.setItem("notif-dismissed", "1");
    setShow(false);
  };
  return (
    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-blue-200 rounded-2xl p-4 sm:p-5 flex items-start gap-3.5 animate-fade-up" data-testid="notification-permission-card">
      <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center">
        <BellRing className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-heading font-bold text-slate-900 text-sm sm:text-base">Enable notifications</h3>
        <p className="text-xs sm:text-sm text-slate-600 mt-1 leading-relaxed">Get instant updates when your submission is verified, rewards are credited, payouts are processed, and new tasks go live.</p>
        <div className="mt-3 flex gap-2">
          <button onClick={allow} data-testid="enable-notifications-btn" className="px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors">Allow Notifications</button>
          <button onClick={later} data-testid="dismiss-notifications-btn" className="px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold text-slate-600 hover:bg-white/70 transition-colors">Maybe Later</button>
        </div>
      </div>
    </div>
  );
}

function UserHome({ user, tasks, dash }) {
  const featured = tasks.slice(0, 3);
  return (
    <div className="space-y-5 animate-fade-up" data-testid="user-home-screen">
      <div className="pt-1">
        <p className="text-sm text-slate-500 font-medium">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</p>
        <h1 className="mt-1 text-3xl sm:text-4xl font-bold font-heading text-slate-900" data-testid="home-welcome-heading">Hi, {user.name?.split(" ")[0]}</h1>
      </div>

      <NotificationPrompt />

      <Link to="/payments" data-testid="home-balance-card" className="block rounded-3xl bg-gradient-to-br from-blue-600 to-indigo-700 p-6 shadow-xl active:scale-[0.99] transition-transform">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-blue-100 uppercase tracking-wider">Available Balance</p>
          <ArrowUpRight className="w-4 h-4 text-blue-200" />
        </div>
        <p className="mt-2 text-4xl font-bold text-white font-heading" data-testid="home-balance">{dash ? inr(dash.available_rewards) : "—"}</p>
        <div className="mt-5 grid grid-cols-3 gap-2">
          <div className="bg-white/10 rounded-xl px-3 py-2.5 border border-white/15">
            <p className="text-[10px] font-semibold text-blue-100 uppercase tracking-wide">Earned</p>
            <p className="text-base font-bold text-white font-heading" data-testid="home-total-earned">{dash ? inr(dash.total_earned) : "—"}</p>
          </div>
          <div className="bg-white/10 rounded-xl px-3 py-2.5 border border-white/15">
            <p className="text-[10px] font-semibold text-blue-100 uppercase tracking-wide">Pending</p>
            <p className="text-base font-bold text-white font-heading" data-testid="home-pending-count">{dash ? dash.pending_verification : "—"}</p>
          </div>
          <div className="bg-white/10 rounded-xl px-3 py-2.5 border border-white/15">
            <p className="text-[10px] font-semibold text-blue-100 uppercase tracking-wide">Completed</p>
            <p className="text-base font-bold text-white font-heading" data-testid="home-completed-count">{dash ? dash.tasks_completed : "—"}</p>
          </div>
        </div>
      </Link>

      <section data-testid="featured-tasks-section">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold font-heading text-slate-900">Available tasks <span className="text-sm font-semibold text-blue-600" data-testid="available-tasks-total">({tasks.length})</span></h2>
          <Link to="/tasks" data-testid="featured-view-all-link" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-800">
            View all <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        {featured.length > 0 ? (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="featured-tasks-grid">
            {featured.map((t, i) => <TaskCard key={t.id} task={t} index={i} />)}
          </div>
        ) : (
          <div className="mt-4 bg-white rounded-2xl border border-slate-200/80 p-8 text-center" data-testid="home-no-tasks">
            <ClipboardList className="w-9 h-9 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">No active tasks right now — check back soon.</p>
          </div>
        )}
      </section>

      <section className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5" data-testid="recent-activity-section">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-bold font-heading text-slate-900">Recent activity</h2>
          <Link to="/account" data-testid="view-all-activity-link" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-800">View all <ChevronRight className="w-4 h-4" /></Link>
        </div>
        {dash?.recent_submissions?.length ? (
          <div className="mt-3 divide-y divide-slate-100">
            {dash.recent_submissions.map((s) => (
              <div key={s.id} className="py-3 flex items-center justify-between gap-3" data-testid={`recent-submission-${s.submission_code}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 text-sm truncate">{s.task_name}</p>
                  <p className="text-xs text-slate-500">{s.submission_code} · {fmtDate(s.created_at)}</p>
                </div>
                <StatusBadge status={s.status} />
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-3 text-center py-6" data-testid="no-activity-yet">
            <p className="text-sm text-slate-500">No activity yet — complete your first task!</p>
            <Link to="/tasks" data-testid="home-browse-tasks-btn" className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-colors">Browse Tasks</Link>
          </div>
        )}
      </section>
    </div>
  );
}

function GuestHome({ tasks, stats }) {
  const featured = tasks.slice(0, 3);
  return (
    <div className="space-y-12 sm:space-y-16">
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 p-6 sm:p-10 lg:p-14 grid lg:grid-cols-2 gap-10 items-center animate-fade-up shadow-2xl" data-testid="home-hero">
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-blue-600/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-16 w-64 h-64 rounded-full bg-indigo-600/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-blue-200 bg-white/10 border border-white/15 backdrop-blur">
            <Sparkles className="w-3.5 h-3.5" /> India's App Testing & Research Platform
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.12] font-heading" data-testid="hero-heading">
            Test Apps. Share Feedback. <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-300">Get Rewarded.</span>
          </h1>
          <p className="mt-5 text-lg sm:text-xl text-blue-100/90 leading-relaxed" data-testid="hero-subheading">
            Discover app testing opportunities, try new apps, share genuine feedback, and earn rewards for completing eligible tasks — paid straight to your UPI.
          </p>
          <p className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-green-500/15 border border-green-400/30 text-green-300 text-sm sm:text-base font-semibold" data-testid="hero-earn-badge">
            <BadgeIndianRupee className="w-4 h-4" /> Earn ₹10+ for each verified app feedback
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/register" data-testid="hero-register-btn" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl font-semibold text-slate-900 bg-white hover:bg-blue-50 transition-colors shadow-lg text-base">
              Start Earning — It's Free <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/tasks" data-testid="hero-view-tasks-btn" className="inline-flex items-center justify-center px-7 py-4 rounded-xl font-semibold text-white border border-white/30 hover:bg-white/10 transition-colors text-base">
              View Available Tasks
            </Link>
          </div>
          <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-blue-200/80">
            <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-green-400" /> 100% free to join</span>
            <span className="flex items-center gap-1.5"><Wallet className="w-4 h-4 text-green-400" /> UPI payouts</span>
            <span className="flex items-center gap-1.5"><ThumbsUp className="w-4 h-4 text-green-400" /> Honest feedback only</span>
          </div>
        </div>
        <div className="relative">
          <img src="https://images.unsplash.com/photo-1551721434-8b94ddff0e6d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900" alt="Person testing a mobile app" data-testid="hero-image" className="rounded-3xl shadow-2xl border border-white/10 w-full object-cover aspect-[4/3]" />
          <div className="absolute -bottom-4 left-4 bg-white rounded-2xl shadow-xl px-5 py-3.5 animate-fade-up" style={{ animationDelay: "200ms" }}>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Tasks available now</p>
            <p className="text-lg font-bold text-slate-900 font-heading" data-testid="hero-tasks-count">{tasks.length} active</p>
          </div>
          <div className="absolute -top-4 right-4 bg-white rounded-2xl shadow-xl px-4 py-3 animate-fade-up flex items-center gap-2" style={{ animationDelay: "350ms" }}>
            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
            <p className="text-sm font-bold text-slate-900 font-heading">Your opinion matters</p>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="stats-strip">
        {[
          { icon: Users, label: "Registered testers", value: stats ? `${stats.users}+` : "—", testid: "stat-users" },
          { icon: CheckCircle2, label: "Tasks verified", value: stats ? `${stats.verified_submissions}+` : "—", testid: "stat-verified" },
          { icon: BadgeIndianRupee, label: "Rewards distributed", value: stats ? inr(stats.rewards_distributed) : "—", testid: "stat-rewards" },
          { icon: ClipboardList, label: "Live tasks", value: String(tasks.length), testid: "stat-live-tasks" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-4 sm:p-5 text-center">
            <s.icon className="w-5 h-5 text-blue-600 mx-auto" />
            <p className="mt-2 text-xl sm:text-2xl font-bold text-slate-900 font-heading" data-testid={s.testid}>{s.value}</p>
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mt-0.5">{s.label}</p>
          </div>
        ))}
      </section>

      <section data-testid="how-it-works-section">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 font-heading">Start earning in 4 simple steps</h2>
          <p className="mt-3 text-base text-slate-600">No experience needed. If you can install an app, you can do this.</p>
        </div>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {STEPS.map((s, i) => (
            <div key={s.title} data-testid={`how-step-${i + 1}`} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm p-5 relative hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
              <span className="absolute top-4 right-4 text-3xl font-bold text-slate-100 font-heading">{i + 1}</span>
              <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-sm">
                <s.icon className="w-5 h-5" />
              </div>
              <h3 className="mt-3.5 font-heading font-semibold text-slate-900">{s.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 max-w-3xl mx-auto" data-testid="how-to-video-block">
          <h3 className="text-center text-xl sm:text-2xl font-bold font-heading text-slate-900">Watch: how to complete a task</h3>
          <div className="mt-5 rounded-3xl overflow-hidden border border-slate-200 shadow-xl bg-slate-900">
            <video data-testid="how-to-video" controls playsInline preload="metadata" className="w-full" src="https://customer-assets-jai6qajn.emergentagent.net/job_review-earn-app/artifacts/7lkyrjta_gemini_generated_video_FC51DCE4.mp4">
              Your browser does not support the video tag.
            </video>
          </div>
          <p className="mt-3 text-center text-sm text-slate-500">Task kaise complete karein aur feedback kaise submit karein — 1 minute ka demo.</p>
        </div>
      </section>

      <section data-testid="guest-featured-tasks-section">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-heading">Live tasks — start now</h2>
            <p className="mt-2 text-base text-slate-600"><span className="font-semibold text-blue-700">{tasks.length}</span> task{tasks.length === 1 ? "" : "s"} waiting for you right now.</p>
          </div>
          <Link to="/tasks" data-testid="guest-view-all-link" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-800">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {featured.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="guest-featured-grid">
            {featured.map((t, i) => <TaskCard key={t.id} task={t} index={i} />)}
          </div>
        )}
      </section>

      <section className="bg-white rounded-3xl border border-slate-200/80 shadow-sm p-6 sm:p-10" data-testid="why-section">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-heading">Why testers choose Equinox 4 Review</h2>
        </div>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {WHY.map((w, i) => (
            <div key={w.title} data-testid={`why-card-${i}`} className="rounded-2xl border border-slate-200/80 bg-slate-50/60 p-5">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <w.icon className="w-5 h-5" />
              </div>
              <h3 className="mt-3.5 font-heading font-semibold text-slate-900">{w.title}</h3>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{w.text}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-xs text-slate-500 max-w-2xl mx-auto leading-relaxed">
          We never ask for a specific star rating or a positive review. Rewards are for completing research tasks and sharing honest feedback — positive, neutral, or negative.
        </p>
      </section>

      <section className="rounded-3xl bg-gradient-to-br from-blue-600 to-indigo-700 px-6 sm:px-12 py-12 sm:py-14 text-center shadow-xl relative overflow-hidden" data-testid="home-cta-section">
        <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <h2 className="relative text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-heading">Your first reward is 10 minutes away</h2>
        <p className="relative mt-3 text-blue-100 max-w-xl mx-auto">Create your free account now and earn <strong className="text-white">₹10+ for each verified app feedback</strong> — your first reward is just one task away.</p>
        <div className="relative mt-7 flex flex-col sm:flex-row justify-center gap-3">
          <Link to="/register" data-testid="cta-register-btn" className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-semibold text-blue-700 bg-white hover:bg-blue-50 transition-colors shadow-lg text-base">Create Free Account</Link>
          <Link to="/login" data-testid="cta-login-btn" className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-semibold text-white border border-white/40 hover:bg-white/10 transition-colors text-base">Login</Link>
        </div>
      </section>

      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 p-3 flex gap-2.5 shadow-[0_-4px_16px_-6px_rgba(15,23,42,0.15)]" data-testid="guest-sticky-cta">
        <Link to="/login" data-testid="sticky-login-btn" className="flex-1 inline-flex items-center justify-center px-4 py-3 rounded-xl text-sm font-semibold text-slate-700 bg-slate-100 border border-slate-200">Login</Link>
        <Link to="/register" data-testid="sticky-register-btn" className="flex-[2] inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-xl text-sm font-semibold text-white bg-blue-600 shadow-sm">Register & Start Earning <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [dash, setDash] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/tasks/public").then((r) => setTasks(r.data)).catch(() => {});
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) api.get("/user/dashboard").then((r) => setDash(r.data)).catch(() => {});
  }, [user]);

  if (user) return <UserHome user={user} tasks={tasks} dash={dash} />;
  return <GuestHome tasks={tasks} stats={stats} />;
}
